import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import {
  PROPERTY_DIRECTORY_COLUMNS,
  toDirectoryEntry,
  type PropertyDirectoryEntry,
  type PropertyDirectoryRow,
} from '../lib/propertyDirectory';

export interface DeploymentSummary {
  active: number;
  maintenance: number;
  offline: number;
  total: number;
}

export interface LocationAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;
}

export interface LocationHierarchy {
  region?: string;
  district?: string;
  zone?: string;
  portfolio?: string;
  property_code?: string;
}

export interface LocationDeployment {
  id: string;
  solution_type: string;
  status: string | null;
  vendor: string | null;
  last_health_check: string | null;
  sla_response_hours: number | null;
  commissioned_date: string | null;
  created_at: string;
  updated_at: string;
  location_id: string;
}

export interface LocationWithSummary {
  id: string;
  customer_id: string;
  name: string;
  type: string;
  address: LocationAddress | null;
  hierarchy: LocationHierarchy | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  timezone: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deployments: LocationDeployment[];
  deploymentSummary: DeploymentSummary;
  /** Property directory row linked through hierarchy.property_code, if any. */
  directory: PropertyDirectoryEntry | null;
  /** Map coordinates: the location's own address first, then the directory. */
  coordinates: { lat: number; lng: number } | null;
}

export interface LocationsResult {
  locations: LocationWithSummary[];
}

// The deployments embed deliberately omits `config`: an open JSONB blob that
// production uses for the Zoho deal id, and that nothing in the portal reads.
// An allowlist containing a free-form object admits whatever is put in it next.
// tests/customerQueryAllowlist.test.ts fails if it comes back.
export const LOCATION_COLUMNS = `
  id, customer_id, name, type, address, hierarchy,
  contact_name, contact_phone, contact_email, timezone,
  status, created_at, updated_at, deleted_at,
  deployments (
    id, solution_type, status, vendor, last_health_check,
    sla_response_hours, commissioned_date, created_at,
    updated_at, location_id
  )
`;

function summariseDeployments(deployments: Array<{ status: string | null }>): DeploymentSummary {
  let active = 0;
  let maintenance = 0;
  let offline = 0;
  for (const d of deployments) {
    if (d.status === 'active') active++;
    else if (d.status === 'degraded' || d.status === 'installing') maintenance++;
    else if (d.status === 'offline') offline++;
  }
  return { active, maintenance, offline, total: deployments.length };
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function resolveCoordinates(
  address: LocationAddress | null,
  directory: PropertyDirectoryEntry | null,
): { lat: number; lng: number } | null {
  const lat = finiteNumber(address?.lat) ?? directory?.lat ?? null;
  const lng = finiteNumber(address?.lng) ?? directory?.lng ?? null;
  if (lat === null || lng === null) return null;
  return { lat, lng };
}

/** Fetches the directory rows for a set of property codes (RLS-scoped). */
export async function fetchDirectoryByCodes(
  codes: string[],
): Promise<Map<string, PropertyDirectoryEntry>> {
  const map = new Map<string, PropertyDirectoryEntry>();
  const unique = [...new Set(codes.filter((c): c is string => !!c))];
  if (unique.length === 0) return map;
  const { data, error } = await supabase
    .from('property_directory_customer_view')
    .select(PROPERTY_DIRECTORY_COLUMNS)
    .in('code', unique);
  if (error) throw error;
  for (const row of (data ?? []) as PropertyDirectoryRow[]) {
    const entry = toDirectoryEntry(row);
    if (entry) map.set(entry.code, entry);
  }
  return map;
}

export function useLocations() {
  const scope = useCustomerScope();

  return useQuery({
    queryKey: ['locations', scope.key],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<LocationsResult> => {
      const { data, error } = await supabase
        .from('locations')
        .select(LOCATION_COLUMNS)
        .in('customer_id', scope.ids)
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;

      const rows = (data ?? []) as unknown as Array<
        Omit<LocationWithSummary, 'deploymentSummary' | 'directory' | 'coordinates'>
      >;
      const directory = await fetchDirectoryByCodes(
        rows.map((l) => l.hierarchy?.property_code ?? ''),
      );

      const locations: LocationWithSummary[] = rows.map((loc) => {
        const entry = loc.hierarchy?.property_code
          ? directory.get(loc.hierarchy.property_code) ?? null
          : null;
        return {
          ...loc,
          deployments: loc.deployments ?? [],
          deploymentSummary: summariseDeployments(loc.deployments ?? []),
          directory: entry,
          coordinates: resolveCoordinates(loc.address, entry),
        };
      });

      return { locations };
    },
    staleTime: 2 * 60 * 1000,
  });
}
